import React, { useState, useEffect, useCallback } from 'react';
import { Square, Loader2, RefreshCw, Clock, CheckCircle, XCircle, AlertCircle, Mail, Globe, ShieldAlert, AlertTriangle } from 'lucide-react';
import canvassingService from '../../services/canvassingService';
import userService from '../../services/userService';
import useEmailStream from '../../hooks/useEmailStream';
import useConfirm from '../../hooks/useConfirm';
import ConfirmDialog from '../ConfirmDialog';
import { useToast } from '../Toast';

// Strip postcode suffix from territory name
function getTerritoryBaseName(memberName) {
  return (memberName || '').replace(/\s*\([A-Z]+\d*\)\s*$/, '').trim().toLowerCase();
}

const FILTER_LABELS = {
  not_attempted: 'Not yet attempted',
  retry_errors: 'Retry errors/blocked',
  all: 'Re-extract all'
};

const EmailTab = () => {
  const [techGroups, setTechGroups] = useState([]);
  const [emailJobs, setEmailJobs] = useState([]);
  const [emailStats, setEmailStats] = useState(null);
  const [memberCounts, setMemberCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedMember, setSelectedMember] = useState('');
  const [filterMode, setFilterMode] = useState('not_attempted');
  const [isStarting, setIsStarting] = useState(false);
  const [activeJobId, setActiveJobId] = useState(null);
  const [error, setError] = useState(null);
  const toast = useToast();

  const { confirmDialog, confirm } = useConfirm();

  const {
    isConnected,
    progress,
    isCompleted,
    hasError,
    completedStats,
    connectToEmailStream,
    resetEmailStream
  } = useEmailStream();

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [terr, jobs, stats, memberCountsResp, techResp, franchiseeResp] = await Promise.all([
        canvassingService.getTerritories(),
        canvassingService.getEmailJobs(),
        canvassingService.getEmailStats(),
        canvassingService.getEmailMemberCounts(),
        userService.getUsers({ role: 'technician', limit: 100, active: true }),
        userService.getUsers({ role: 'franchisee', limit: 100, active: true })
      ]);
      const allTerritories = terr.territories || [];
      setEmailJobs(jobs.jobs || []);
      setEmailStats(stats);
      setMemberCounts(memberCountsResp.counts || {});

      const franchisees = franchiseeResp.users || [];
      const techs = techResp.users || [];

      // Build name->territory lookup
      const nameToTerritory = {};
      for (const t of allTerritories) {
        nameToTerritory[getTerritoryBaseName(t.member_name)] = t;
      }

      // Group by franchise
      const groups = franchisees.map(f => {
        const franchiseTechs = techs
          .filter(t => t.assigned_franchisee_id === f.id)
          .map(t => {
            const territory = nameToTerritory[t.name.toLowerCase()];
            return { id: t.id, name: t.name, member_id: territory?.member_id || null };
          });

        const franchiseeTerritory = nameToTerritory[f.name.toLowerCase()];
        const franchiseeEntry = {
          id: f.id, name: f.name, member_id: franchiseeTerritory?.member_id || null,
          isFranchisee: true
        };

        return {
          id: f.id,
          name: f.name,
          company: f.company_name || '',
          franchisee: franchiseeEntry,
          technicians: franchiseTechs
        };
      });

      setTechGroups(groups);
    } catch (err) {
      setError('Failed to load data');
      console.error('loadData error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Reconnect to running job on mount
  useEffect(() => {
    const reconnectIfRunning = async () => {
      try {
        const data = await canvassingService.getEmailJobs(5);
        const jobs = data.jobs || [];
        const runningJob = jobs.find(j => j.status === 'running');
        if (runningJob) {
          setActiveJobId(runningJob.id);
          connectToEmailStream(runningJob.id);
        }
      } catch {}
    };
    reconnectIfRunning();
  }, [connectToEmailStream]);

  // Poll active job as fallback
  useEffect(() => {
    if (!activeJobId || isCompleted || hasError) return;

    const poll = setInterval(async () => {
      try {
        const data = await canvassingService.getEmailJob(activeJobId);
        const job = data.job;
        if (job && (job.status === 'completed' || job.status === 'failed' || job.status === 'cancelled')) {
          clearInterval(poll);
          loadData();
          if (job.status === 'completed') {
            toast.success(`Email extraction complete — ${job.emails_found || 0} emails found from ${job.processed_leads || 0} leads`);
          } else {
            toast.error(`Extraction ${job.status}${job.error_message ? ` — ${job.error_message}` : ''}`);
          }
          setActiveJobId(null);
        }
      } catch {}
    }, 5000);

    return () => clearInterval(poll);
  }, [activeJobId, isCompleted, hasError, loadData]);

  // Refresh on completion
  useEffect(() => {
    if (isCompleted) {
      loadData();
      const stats = completedStats || {};
      toast.success(`Email extraction complete — ${stats.emailsFound || 0} emails found from ${stats.processed || 0} leads`);
    }
  }, [isCompleted, completedStats, loadData]);

  useEffect(() => {
    if (hasError) {
      loadData();
      toast.error('Email extraction failed — check logs for details');
    }
  }, [hasError, loadData]);

  /**
   * Get the website lead count for a member_id from the memberCounts map
   */
  const getWebsiteCount = (memberId) => {
    if (!memberId || !memberCounts[memberId]) return 0;
    return memberCounts[memberId].with_website || 0;
  };

  /**
   * Get aggregate website count for a franchise group (all members combined)
   */
  const getGroupWebsiteCount = (group) => {
    const allMembers = [group.franchisee, ...group.technicians];
    return allMembers.reduce((sum, m) => sum + getWebsiteCount(m.member_id), 0);
  };

  // Resolve selected member label for confirm dialog
  const getSelectedMemberLabel = () => {
    if (!selectedMember) return 'All Members';
    if (selectedMember.startsWith('f:')) {
      const group = techGroups.find(g => g.id === selectedMember.slice(2));
      return group ? `${group.name} — all` : 'All Members';
    }
    const allMembers = techGroups.flatMap(g => [g.franchisee, ...g.technicians]);
    const member = allMembers.find(m => m.id === selectedMember);
    return member ? member.name : 'All Members';
  };

  /**
   * Estimate how many leads will be processed based on selected member + filter
   */
  const getEstimatedCount = () => {
    if (!selectedMember) {
      if (filterMode === 'not_attempted') return emailStats?.pending || 0;
      if (filterMode === 'retry_errors') return (emailStats?.blocked || 0) + (emailStats?.errored || 0);
      return emailStats?.with_website || 0;
    }

    // Resolve member IDs for the selection
    let memberIds = [];
    if (selectedMember.startsWith('f:')) {
      const group = techGroups.find(g => g.id === selectedMember.slice(2));
      if (group) {
        memberIds = [group.franchisee, ...group.technicians].filter(m => m.member_id).map(m => m.member_id);
      }
    } else {
      const allMembers = techGroups.flatMap(g => [g.franchisee, ...g.technicians]);
      const member = allMembers.find(m => m.id === selectedMember);
      if (member?.member_id) memberIds = [member.member_id];
    }

    let total = 0;
    for (const mid of memberIds) {
      const c = memberCounts[mid];
      if (!c) continue;
      if (filterMode === 'not_attempted') total += c.not_attempted || 0;
      else if (filterMode === 'retry_errors') total += c.failed || 0;
      else total += c.with_website || 0;
    }
    return total;
  };

  const handleStartExtraction = async () => {
    const memberLabel = getSelectedMemberLabel();
    const filterLabel = FILTER_LABELS[filterMode] || filterMode;

    const estimatedCount = getEstimatedCount();
    const confirmed = await confirm({
      title: 'Start Email Extraction',
      message: `This will visit ${estimatedCount.toLocaleString()} business websites and extract email addresses.\n\nMember: ${memberLabel}\nFilter: ${filterLabel}`,
      variant: 'info',
      confirmLabel: 'Start Extraction'
    });
    if (!confirmed) return;

    try {
      setIsStarting(true);
      setError(null);
      resetEmailStream();

      const extractParams = { filterMode };

      // Resolve member ID
      if (selectedMember) {
        if (selectedMember.startsWith('f:')) {
          const group = techGroups.find(g => g.id === selectedMember.slice(2));
          if (group) {
            const ids = [group.franchisee, ...group.technicians].filter(m => m.member_id).map(m => m.member_id);
            if (ids.length > 0) extractParams.memberId = ids[0];
          }
        } else {
          const allMembers = techGroups.flatMap(g => [g.franchisee, ...g.technicians]);
          const member = allMembers.find(m => m.id === selectedMember);
          if (member?.member_id) extractParams.memberId = member.member_id;
        }
      }

      const result = await canvassingService.startEmailExtraction(extractParams);

      if (result.jobId) {
        setActiveJobId(result.jobId);
        await connectToEmailStream(result.jobId);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to start email extraction');
    } finally {
      setIsStarting(false);
    }
  };

  const handleCancelExtraction = async () => {
    if (!activeJobId) return;
    try {
      await canvassingService.cancelEmailExtraction(activeJobId);
    } catch {
      setError('Failed to cancel extraction');
    }
  };

  const isRunning = progress && !isCompleted && !hasError;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="animate-spin text-pest-blue" size={24} />
        <span className="ml-2 text-gray-500">Loading...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center justify-between">
          <span className="text-red-700 text-sm">{error}</span>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600"><XCircle size={16} /></button>
        </div>
      )}

      {/* Stats Dashboard — updates live during extraction */}
      {emailStats && (() => {
        // Number() required — MySQL2 returns SUM values as strings, and "206" + 17 = "20617" not 223
        const baseEmails = Number(emailStats.with_email) || 0;
        const basePending = Number(emailStats.pending) || 0;
        const baseBlocked = Number(emailStats.blocked) || 0;
        const baseErrors = Number(emailStats.errored) || 0;

        const liveEmailsFound = isRunning && progress ? baseEmails + (progress.emailsFound || 0) : baseEmails;
        const livePending = isRunning && progress ? Math.max(0, basePending - (progress.processed || 0)) : basePending;
        const liveBlocked = isRunning && progress ? baseBlocked + (progress.blocked || 0) : baseBlocked;
        const liveErrors = isRunning && progress ? baseErrors + (progress.errors || 0) : baseErrors;

        return (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <StatCard label="With Website" value={emailStats.with_website || 0} icon={<Globe size={16} />} color="blue" />
            <StatCard label="Emails Found" value={liveEmailsFound} icon={<Mail size={16} />} color="green" pulse={isRunning} />
            <StatCard label="Not Attempted" value={livePending} icon={<Clock size={16} />} color="gray" />
            <StatCard label="Blocked" value={liveBlocked} icon={<ShieldAlert size={16} />} color="orange" />
            <StatCard label="Errors" value={liveErrors} icon={<AlertTriangle size={16} />} color="red" />
          </div>
        );
      })()}

      {/* Configuration */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h3 className="font-semibold text-gray-800 mb-4">Email Extraction Settings</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          {/* Member selector — shows website lead count */}
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">Member</label>
            <select
              value={selectedMember}
              onChange={(e) => setSelectedMember(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg text-sm"
              disabled={isRunning}
            >
              <option value="">All Members ({emailStats?.with_website?.toLocaleString() || 0} with website)</option>
              {techGroups.map(group => {
                const groupCount = getGroupWebsiteCount(group);
                return (
                  <optgroup key={group.id} label={`${group.company || group.name} (${groupCount})`}>
                    {/* Franchisee entry */}
                    <option value={`f:${group.id}`}>
                      {group.name} — all ({groupCount} with website)
                    </option>
                    {/* Individual technicians */}
                    {group.technicians.map(tech => {
                      const techCount = getWebsiteCount(tech.member_id);
                      return (
                        <option key={tech.id} value={tech.id}>
                          {tech.name} ({techCount} with website)
                        </option>
                      );
                    })}
                  </optgroup>
                );
              })}
            </select>
          </div>

          {/* Filter mode */}
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">Filter</label>
            <select
              value={filterMode}
              onChange={(e) => setFilterMode(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg text-sm"
              disabled={isRunning}
            >
              <option value="not_attempted">Not yet attempted</option>
              <option value="retry_errors">Retry errors/blocked</option>
              <option value="all">Re-extract all</option>
            </select>
          </div>
        </div>

        {/* Start/Cancel button */}
        <div className="flex items-center space-x-3">
          {isRunning ? (
            <button
              onClick={handleCancelExtraction}
              className="flex items-center space-x-2 px-6 py-2.5 bg-red-500 text-white rounded-lg hover:bg-red-600 text-sm font-medium"
            >
              <Square size={16} /><span>Cancel Extraction</span>
            </button>
          ) : (
            <button
              onClick={handleStartExtraction}
              disabled={isStarting}
              className="flex items-center space-x-2 px-6 py-2.5 bg-pest-blue text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50"
            >
              {isStarting ? <Loader2 size={16} className="animate-spin" /> : <Mail size={16} />}
              <span>{isStarting ? 'Starting...' : 'Start Email Extraction'}</span>
            </button>
          )}

          {isRunning && isConnected && (
            <span className="flex items-center space-x-2 text-xs text-gray-600">
              <Loader2 size={14} className="animate-spin text-blue-600" />
              <span className="text-blue-600 font-medium">
                {progress?.currentBusiness
                  ? `Extracting from ${progress.currentBusiness}`
                  : 'Extracting...'
                }
              </span>
              <span className="w-2 h-2 bg-green-500 rounded-full"></span>
            </span>
          )}
        </div>
      </div>

      {/* Live Progress */}
      {progress && (() => {
        const percent = progress.total > 0
          ? Math.round(progress.processed / progress.total * 100) : 0;

        return (
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h3 className="font-semibold text-gray-800 mb-3">
              {isCompleted ? 'Extraction Complete' : hasError ? 'Extraction Failed' : 'Live Progress'}
            </h3>

            <div className={`rounded-lg border p-3 ${
              isCompleted ? 'border-green-200 bg-green-50' :
              hasError ? 'border-red-200 bg-red-50' :
              'border-blue-200 bg-blue-50'
            }`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-2">
                  {isCompleted ? (
                    <CheckCircle size={16} className="text-green-600" />
                  ) : hasError ? (
                    <XCircle size={16} className="text-red-600" />
                  ) : (
                    <Loader2 size={16} className="text-blue-600 animate-spin" />
                  )}
                  <span className={`text-sm font-medium ${
                    isCompleted ? 'text-green-700' : hasError ? 'text-red-700' : 'text-blue-700'
                  }`}>
                    Email Extraction
                  </span>
                </div>
                <span className={`text-xs font-medium ${
                  isCompleted ? 'text-green-600' : hasError ? 'text-red-600' : 'text-blue-600'
                }`}>
                  {isCompleted ? 'Complete' : hasError ? 'Failed' : `${percent}%`}
                </span>
              </div>

              {/* Progress bar */}
              <div className="w-full bg-white/60 rounded-full h-1.5 mb-2">
                <div
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    isCompleted ? 'bg-green-500' : hasError ? 'bg-red-500' : 'bg-blue-500'
                  }`}
                  style={{ width: `${isCompleted ? 100 : percent}%` }}
                />
              </div>

              {/* Details */}
              <div className="flex items-center justify-between text-xs">
                <span className={isCompleted ? 'text-green-600' : hasError ? 'text-red-600' : 'text-blue-600'}>
                  <strong>{progress.processed || 0}/{progress.total || 0}</strong> websites processed
                  {!isCompleted && !hasError && progress.currentBusiness && (
                    <> — <em>{progress.currentBusiness}</em></>
                  )}
                </span>
                <span className="text-gray-500">
                  <strong className="text-green-600">{progress.emailsFound || 0}</strong> emails · {progress.pagesCrawled || 0} pages · {progress.blocked || 0} blocked · {progress.errors || 0} errors
                </span>
              </div>
            </div>

            {/* Social Media Counts Card */}
            {(() => {
              const s = progress.socials || completedStats?.socials || {};
              const totalSocials = Object.values(s).reduce((sum, v) => sum + (v || 0), 0);
              if (totalSocials === 0 && !isRunning) return null;
              return (
                <div className="mt-3 rounded-lg border border-purple-200 bg-purple-50 px-4 py-3">
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="text-xs font-medium text-purple-700">Social Profiles Found</span>
                    <span className="text-lg font-semibold text-purple-800">{totalSocials}</span>
                  </div>
                  <div className="flex items-center space-x-4 flex-wrap">
                    <SocialCounter platform="facebook" count={s.facebook || 0} />
                    <SocialCounter platform="twitter" count={s.twitter || 0} />
                    <SocialCounter platform="instagram" count={s.instagram || 0} />
                    <SocialCounter platform="linkedin" count={s.linkedin || 0} />
                    <SocialCounter platform="youtube" count={s.youtube || 0} />
                    <SocialCounter platform="tiktok" count={s.tiktok || 0} />
                  </div>
                </div>
              );
            })()}

            {/* Completed summary stats */}
            {isCompleted && completedStats && (
              <div className="mt-3 grid grid-cols-2 md:grid-cols-5 gap-3">
                <SmallStatCard label="Processed" value={completedStats.processed || 0} color="gray" />
                <SmallStatCard label="Emails Found" value={completedStats.emailsFound || 0} color="green" />
                <SmallStatCard label="No Email" value={(completedStats.processed || 0) - (completedStats.emailsFound || 0) - (completedStats.blocked || 0) - (completedStats.errors || 0)} color="blue" />
                <SmallStatCard label="Blocked" value={completedStats.blocked || 0} color="orange" />
                <SmallStatCard label="Pages Crawled" value={completedStats.pagesCrawled || 0} color="purple" />
              </div>
            )}
          </div>
        );
      })()}

      {/* Job History */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <h3 className="font-semibold text-gray-800">Extraction History</h3>
          <button onClick={loadData} className="text-gray-400 hover:text-gray-600">
            <RefreshCw size={16} />
          </button>
        </div>

        {emailJobs.length === 0 ? (
          <div className="text-center py-8 text-gray-500 text-sm">
            No email extractions yet. Start one above.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-4 py-2 font-medium text-gray-600">ID</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-600">Status</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-600">Member</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-600">Filter</th>
                  <th className="text-right px-4 py-2 font-medium text-gray-600">Processed</th>
                  <th className="text-right px-4 py-2 font-medium text-gray-600">Emails</th>
                  <th className="text-center px-4 py-2 font-medium text-gray-600">Socials</th>
                  <th className="text-right px-4 py-2 font-medium text-gray-600">Blocked</th>
                  <th className="text-right px-4 py-2 font-medium text-gray-600">Errors</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-600">Started</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {emailJobs.map((job) => (
                  <tr key={job.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2 text-gray-500">#{job.id}</td>
                    <td className="px-4 py-2">
                      <StatusBadge status={job.status} />
                    </td>
                    <td className="px-4 py-2 text-gray-700">{job.member_name || 'All'}</td>
                    <td className="px-4 py-2 text-gray-500 text-xs">{FILTER_LABELS[job.filter_mode] || job.filter_mode || '-'}</td>
                    <td className="px-4 py-2 text-right text-gray-700">{job.processed_leads || 0}/{job.total_leads || 0}</td>
                    <td className="px-4 py-2 text-right text-green-600 font-medium">{job.emails_found || 0}</td>
                    <td className="px-4 py-2">
                      <JobSocialIcons socials={job.socials_found} />
                    </td>
                    <td className="px-4 py-2 text-right text-orange-600">{job.leads_blocked || 0}</td>
                    <td className="px-4 py-2 text-right text-red-600">{job.leads_errored || 0}</td>
                    <td className="px-4 py-2 text-gray-500 text-xs">
                      {job.started_at ? new Date(job.started_at).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' }) : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ConfirmDialog {...confirmDialog} />

    </div>
  );
};

const StatCard = ({ label, value, icon, color = 'gray', pulse = false }) => {
  const colorClasses = {
    gray: 'bg-gray-50 border-gray-200 text-gray-800',
    green: 'bg-green-50 border-green-200 text-green-800',
    blue: 'bg-blue-50 border-blue-200 text-blue-800',
    orange: 'bg-orange-50 border-orange-200 text-orange-800',
    red: 'bg-red-50 border-red-200 text-red-800',
    purple: 'bg-purple-50 border-purple-200 text-purple-800'
  };

  return (
    <div className={`rounded-lg border px-4 py-3 ${colorClasses[color]} ${pulse ? 'ring-2 ring-green-300 ring-opacity-50' : ''}`}>
      <div className="flex items-center space-x-2 mb-1">
        {icon}
        <span className="text-xs text-gray-500">{label}</span>
      </div>
      <div className="text-xl font-semibold">{Number(value).toLocaleString()}</div>
    </div>
  );
};

const SmallStatCard = ({ label, value, color = 'gray' }) => {
  const colorClasses = {
    gray: 'bg-gray-50 text-gray-800',
    green: 'bg-green-50 text-green-800',
    blue: 'bg-blue-50 text-blue-800',
    orange: 'bg-orange-50 text-orange-800',
    red: 'bg-red-50 text-red-800',
    purple: 'bg-purple-50 text-purple-800'
  };

  return (
    <div className={`rounded-lg px-3 py-2 ${colorClasses[color]}`}>
      <div className="text-xs text-gray-500">{label}</div>
      <div className="text-lg font-semibold">{value}</div>
    </div>
  );
};

const StatusBadge = ({ status }) => {
  const styles = {
    pending: 'bg-gray-100 text-gray-600',
    running: 'bg-blue-100 text-blue-700',
    completed: 'bg-green-100 text-green-700',
    failed: 'bg-red-100 text-red-700',
    cancelled: 'bg-orange-100 text-orange-700'
  };

  const icons = {
    pending: <Clock size={12} />,
    running: <Loader2 size={12} className="animate-spin" />,
    completed: <CheckCircle size={12} />,
    failed: <XCircle size={12} />,
    cancelled: <AlertCircle size={12} />
  };

  return (
    <span className={`inline-flex items-center space-x-1 px-2 py-0.5 rounded-full text-xs font-medium ${styles[status] || styles.pending}`}>
      {icons[status]}
      <span>{status}</span>
    </span>
  );
};

// Tiny inline social media SVG icons
const SOCIAL_ICONS = {
  facebook: <svg width="12" height="12" viewBox="0 0 24 24" fill="#1877F2"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>,
  twitter: <svg width="12" height="12" viewBox="0 0 24 24" fill="#000"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>,
  instagram: <svg width="12" height="12" viewBox="0 0 24 24" fill="#E4405F"><path d="M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678a6.162 6.162 0 100 12.324 6.162 6.162 0 100-12.324zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405a1.441 1.441 0 11-2.882 0 1.441 1.441 0 012.882 0z"/></svg>,
  linkedin: <svg width="12" height="12" viewBox="0 0 24 24" fill="#0A66C2"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>,
  youtube: <svg width="12" height="12" viewBox="0 0 24 24" fill="#FF0000"><path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>,
  tiktok: <svg width="12" height="12" viewBox="0 0 24 24" fill="#000"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg>
};

/**
 * Social media counter — shows icon + count for a platform (used in live progress)
 */
const SocialCounter = ({ platform, count }) => {
  if (count === 0) return (
    <span className="flex items-center space-x-1 opacity-30">
      {SOCIAL_ICONS[platform]}
      <span className="text-xs text-gray-400">0</span>
    </span>
  );
  return (
    <span className="flex items-center space-x-1">
      {SOCIAL_ICONS[platform]}
      <span className="text-xs font-medium text-gray-700">{count}</span>
    </span>
  );
};

/**
 * Social icons row for job history table — shows icons with counts from stored JSON
 */
const JobSocialIcons = ({ socials }) => {
  let s = {};
  if (typeof socials === 'string') {
    try { s = JSON.parse(socials); } catch { s = {}; }
  } else if (socials && typeof socials === 'object') {
    s = socials;
  }

  const total = Object.values(s).reduce((sum, v) => sum + (v || 0), 0);
  if (total === 0) return <span className="text-gray-300 text-xs">-</span>;

  return (
    <div className="flex items-center justify-center space-x-1.5">
      {Object.entries(s).map(([platform, count]) => (
        count > 0 && SOCIAL_ICONS[platform] ? (
          <span key={platform} className="flex items-center space-x-0.5" title={`${platform}: ${count}`}>
            {SOCIAL_ICONS[platform]}
            <span className="text-xs text-gray-600">{count}</span>
          </span>
        ) : null
      ))}
    </div>
  );
};

export default EmailTab;
